import { computed, ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useAppConfigStore } from '@stores/appConfig.store';
import { format, addMonths, endOfMonth, isBefore, parseISO } from 'date-fns';
import { useBookingQueryStore } from '@stores/bookingQuery.store';

import useHttp from '@/utils/httpClient';
const http = useHttp();

const appConfigStore = useAppConfigStore();
const { applicationSetup } = storeToRefs(appConfigStore);
const bookingQueryStore = useBookingQueryStore();
const { query } = storeToRefs(bookingQueryStore);

export const useRatesStore = defineStore('rates', () => {
  const ratesDataCache = ref({});
  const packageId = ref(0);
  const offerId = ref(0);
  const cacheParams = ref({});

  const ratesData = computed(() => {
    let ratesData = {
      bar: {},
      unavailable_days: [],
    };
    const cacheKey = cacheKeyFromParams.value;
    const cache = ratesDataCache.value[cacheKey];
    for (let key in cache) {
      let barCache = cache[key].bar;
      let unavailableDaysCache = cache[key].unavailable_days;
      if (cache[key] === 'dirty') {
        barCache = {};
        unavailableDaysCache = [];
      }
      ratesData.bar = { ...ratesData.bar, ...barCache };
      ratesData.unavailable_days = [
        ...ratesData.unavailable_days,
        ...unavailableDaysCache,
      ];
    }
    return ratesData;
  });

  const getCalendarRates = async (
    month,
    year,
    options = {},
    numberOfMonths = 0,
  ) => {
    const start_date = format(new Date(year, month, 1), 'yyyy-MM-dd');
    const end_date = format(
      endOfMonth(addMonths(new Date(start_date), 2)),
      'yyyy-MM-dd',
    );

    if (!applicationSetup.value.currency) {
      return;
    }

    const params = {
      hotel_id: applicationSetup.value.hotel_id,
      currency: applicationSetup.value.currency,
      adults: options.adults ?? 2,
      children: options.children ?? 0,
      babies: options.babies ?? 0,
      number_rooms: options.number_rooms ?? 1,
      start_date: start_date,
      end_date: end_date,
      promotion_code: query.value.promotion_code,
      campaign_code: query.value.campaign_code,
    };

    if (query.value.filterOffers) {
      params.filter_offers = query.value.filterOffers;
    }

    cacheParams.value = params;

    let keyMissing = getMissingOnCache(month, year, numberOfMonths);
    if (keyMissing) {
      let exp = keyMissing.split('-');
      params.start_date = format(new Date(exp[0], exp[1], 1), 'yyyy-MM-dd');
      params.end_date = format(
        endOfMonth(addMonths(new Date(params.start_date), 2)),
        'yyyy-MM-dd',
      );

      if (numberOfMonths > 0) {
        params.end_date = format(
          endOfMonth(addMonths(new Date(params.start_date), numberOfMonths)),
          'yyyy-MM-dd',
        );
      }
      const cacheKey = cacheKeyFromParams.value;
      ratesDataCache.value[cacheKey] = {
        ...ratesDataCache.value[cacheKey],
        ...getDataFromResponse(false, params.start_date, params.end_date),
      };
      const rsp = await getRates(params);
      ratesDataCache.value[cacheKey] = {
        ...ratesDataCache.value[cacheKey],
        ...getDataFromResponse(rsp.data, params.start_date, params.end_date),
      };
    }
  };

  const getMonthsBetweenDates = (startDate, endDate) => {
    let start = parseISO(startDate);
    const end = parseISO(endDate);
    const dates = [];
    while (isBefore(start, end) || start.getTime() === end.getTime()) {
      dates.push(format(start, 'yyyy-MM'));
      start = addMonths(start, 1);
    }
    return dates;
  };

  const getDataFromResponse = (rsp, startDate, endDate) => {
    let datesFromResponse = {};
    const dates = getMonthsBetweenDates(startDate, endDate);
    for (let date of dates) {
      if (!rsp) {
        datesFromResponse[date] = 'dirty';
        continue;
      }
      if (!rsp[date]) {
        datesFromResponse[date] = {
          bar: {},
          unavailable_days: [],
        };
        continue;
      }
      datesFromResponse[date] = {
        bar: rsp[date].bar,
        unavailable_days: rsp[date].unavailable_days,
      };
    }
    return datesFromResponse;
  };

  const getRates = async (params) => {
    return http
      .get(
        params.hotel_id + '/calendar/' + packageId.value + '/' + offerId.value,
        params,
      )
      .then((rsp) => {
        return rsp;
      });
  };

  const getMissingOnCache = (month, year, numberOfMonths = 0) => {
    let missing = false;
    numberOfMonths = numberOfMonths > 0 ? numberOfMonths : 3;
    for (let i = 0; i < numberOfMonths; i++) {
      let current = addMonths(new Date(year, month, 1), i);
      let currentKey =
        format(current, 'yyyy') + '-' + (parseInt(format(current, 'MM')) - 1);
      current = format(current, 'yyyy-MM');
      if (
        !ratesDataCache.value[cacheKeyFromParams.value] ||
        !ratesDataCache.value[cacheKeyFromParams.value][current]
      ) {
        missing = currentKey;
        break;
      }
    }
    return missing;
  };

  const cacheKeyFromParams = computed(() => {
    if (!cacheParams.value.occupancy) {
      return (
        'p_' +
        packageId.value +
        'o_' +
        offerId.value +
        'a_' +
        cacheParams.value.adults +
        'c_' +
        cacheParams.value.children +
        'b_' +
        cacheParams.value.babies +
        'cur_' +
        cacheParams.value.currency +
        'key_' +
        cacheParams.value.apikey +
        'campaign_' +
        cacheParams.value.campaign_code +
        'pc_' +
        cacheParams.value.promotion_code
      );
    }
    return (
      'p_' +
      packageId.value +
      'o_' +
      offerId.value +
      'occup_' +
      cacheParams.value.occupancy +
      'cur_' +
      cacheParams.value.currency +
      'campaign_' +
      cacheParams.value.campaign_code +
      'pc_' +
      cacheParams.value.promotion_code
    );
  });

  const getBar = async (month, year, numberOfMonths) => {
    let params = {
      adults: 2,
      children: 0,
      babies: 0,
      number_rooms: 1,
    };

    await getCalendarRates(month, year, params, numberOfMonths);
  };

  return {
    ratesData,
    getBar,
  };
});
